// src/components/devices/LockDiscovery.js
import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Search,
  Wifi,
  Battery,
  ChevronRight,
  Radio
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


/**
 * Component for discovering and initializing new smart locks
 * Apple Home-style mobile-first design
 */
const LockDiscovery = ({ onLockInitialized }) => {
  const {
    startSmartLockScan,
    stopSmartLockScan,
    getScanStatus,
    initializeSmartLock,
    initializingLockMac
  } = useContext(AppContext);

  const [isScanning, setIsScanning] = useState(false);
  const [discoveredLocks, setDiscoveredLocks] = useState([]);
  const [selectedLock, setSelectedLock] = useState(null);
  const [showInitSheet, setShowInitSheet] = useState(false);
  const [lockAlias, setLockAlias] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);

  // Start scan for locks
  const handleStartScan = async () => {
    try {
      setIsScanning(true);
      setDiscoveredLocks([]);
      await startSmartLockScan(15000);
    } catch (error) {
      console.error('Error starting scan:', error);
      setIsScanning(false);
    }
  };

  // Stop scan for locks
  const handleStopScan = async () => {
    try {
      await stopSmartLockScan();
      setIsScanning(false);
    } catch (error) {
      console.error('Error stopping scan:', error);
    }
  };

  // Handle initialize lock
  const handleInitializeLock = (lock) => {
    setSelectedLock(lock);
    setLockAlias(lock.lockAlias || lock.lockName || lock.lockMac);
    setShowInitSheet(true);
  };

  // Confirm initialization
  const handleConfirmInitialize = async () => {
    if (selectedLock && !isInitializing && !initializingLockMac) {
      try {
        setIsInitializing(true);
        setShowInitSheet(false);

        await initializeSmartLock(selectedLock.lockMac, lockAlias);

        setSelectedLock(null);
        setLockAlias('');
        setDiscoveredLocks([]);
        if (onLockInitialized) {
          onLockInitialized();
        }
      } catch (error) {
        console.error('Error initializing lock:', error);
        setShowInitSheet(true);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  // Get signal strength indicator
  const getSignalPill = (rssi) => {
    if (rssi === undefined) return null;
    const color = rssi > -50 ? 'bg-green-500' : rssi > -70 ? 'bg-yellow-500' : 'bg-red-500';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${color}`}>
        <Wifi className="h-2.5 w-2.5" />
        {rssi}
      </span>
    );
  };

  // Get battery indicator
  const getBatteryPill = (battery) => {
    if (battery === undefined) return null;
    const color = battery < 20 ? 'bg-red-500' : 'bg-green-500';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${color}`}>
        <Battery className="h-2.5 w-2.5" />
        {battery}%
      </span>
    );
  };

  // Poll scan status to get discovered locks
  useEffect(() => {
    let interval;

    const pollScanStatus = async () => {
      try {
        const status = await getScanStatus();
        if (status.discoveredLocks && status.discoveredLocks.length > 0) {
          const unpaired = status.discoveredLocks.filter(lock => !lock.paired);
          setDiscoveredLocks(unpaired);
        } else {
          setDiscoveredLocks([]);
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    };

    if (isScanning) {
      pollScanStatus();
      interval = setInterval(pollScanStatus, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isScanning, getScanStatus]);

  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">
            {isScanning
              ? 'Scanning...'
              : discoveredLocks.length > 0
              ? `${discoveredLocks.length} found`
              : 'Ready to scan'}
          </p>
        </div>
        <Button
          onClick={isScanning ? handleStopScan : handleStartScan}
          className="h-10 px-6"
        >
          <Radio className={`h-4 w-4 mr-2 ${isScanning ? 'animate-pulse' : ''}`} />
          {isScanning ? 'Stop' : 'Add'}
        </Button>
      </div>

      {/* Content */}
      <div>
        {!isScanning && discoveredLocks.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Search className="h-10 w-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Scan for Smart Locks</h3>
            <p className="text-sm text-muted-foreground max-w-[280px] mx-auto">
              Make sure your locks are in pairing mode and within range
            </p>
          </div>
        ) : (
          /* Scan Results */
          <div className="space-y-6">
            {discoveredLocks.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {isScanning
                    ? 'Searching for locks...'
                    : 'No locks found'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {discoveredLocks.map((lock) => {
                  const isInitializingThisLock = isInitializing || initializingLockMac === lock.lockMac;
                  
                  return (
                    <motion.div
                      key={lock.lockMac}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl bg-white/70 backdrop-blur shadow-sm ring-1 ring-black/5 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-sm truncate">
                                {lock.lockName || 'Unnamed Lock'}
                              </h3>
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-600 rounded-full">
                                New
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                              <span className="font-mono">{lock.lockMac}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              {getSignalPill(lock.rssi)}
                              {getBatteryPill(lock.battery)}
                            </div>
                          </div>

                          <Button
                            onClick={() => handleInitializeLock(lock)}
                            disabled={isInitializingThisLock}
                            className="h-10 px-5 ml-3 shrink-0"
                          >
                            {isInitializingThisLock ? (
                              <>
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                                <span className="text-xs">Initializing</span>
                              </>
                            ) : (
                              <>
                                <span className="text-xs">Add</span>
                                <ChevronRight className="h-3 w-3 ml-1" />
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Sheet for Naming Lock */}
      <AnimatePresence>
        {showInitSheet && selectedLock && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => !isInitializing && setShowInitSheet(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              className="fixed bottom-0 left-0 right-0 max-h-[85dvh] overflow-y-auto bg-background/95 backdrop-blur-xl rounded-t-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-6 space-y-6">
                {/* Handle */}
                <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mb-2" />

                {/* Header */}
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-1">Name this lock</h3>
                  <p className="text-sm text-muted-foreground">
                    Give your lock a recognizable name
                  </p>
                </div>

                {/* Lock Info */}
                <div className="p-4 rounded-xl bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">MAC Address</span>
                    <span className="font-mono">{selectedLock.lockMac}</span>
                  </div>
                  {selectedLock.rssi !== undefined && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Signal</span>
                      <span className="font-mono">{selectedLock.rssi} dBm</span>
                    </div>
                  )}
                  {selectedLock.battery !== undefined && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Battery</span>
                      <span className="font-mono">{selectedLock.battery}%</span>
                    </div>
                  )}
                </div>

                {/* Name Input */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Lock Name</Label>
                  <Input
                    type="text"
                    value={lockAlias}
                    onChange={(e) => setLockAlias(e.target.value)}
                    placeholder="e.g., Front Door, Main Gate"
                    className="h-12 text-base"
                    autoFocus
                    disabled={isInitializing}
                  />
                </div>

                {/* Helper Text */}
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Keep the lock awake during initialization by pressing any button on the lock.
                  </p>
                </div>
              </div>

              {/* Sticky Bottom Actions */}
              <div className="sticky bottom-0 bg-background/95 backdrop-blur-xl border-t p-4 pb-safe">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => !isInitializing && setShowInitSheet(false)}
                    disabled={isInitializing}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 h-12"
                    onClick={handleConfirmInitialize}
                    disabled={!lockAlias.trim() || isInitializing}
                  >
                    {isInitializing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Initializing...
                      </>
                    ) : (
                      'Add Lock'
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LockDiscovery;
